import {neverland as $, html, useState} from 'neverland';
import { signUp, signIn, confirmSignUp, resendConfirmationCode } from '../lib/cognito.js';
import { PasswordChecklist, passwordMeetsAllRules } from '../lib/passwordRules.js';
import { buildRegistrationEntry, sendApprovalNotification } from '../lib/approval.js';

function getContactEmail(userData) {
  if (!userData.contacts || !Array.isArray(userData.contacts)) return null;
  for (let c of userData.contacts) {
    if (c.email && c.email.trim()) return c.email.trim();
  }
  return null;
}

const RegisterPage = $(function(auth, setAuth, theme) {
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('register'); // 'register' | 'verify'
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [registeredPassword, setRegisteredPassword] = useState('');
  const [noContactEmail, setNoContactEmail] = useState(false);
  const [isContactPerson, setIsContactPerson] = useState(false);
  const [pw, setPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  function getLogo() {
    if (theme && theme.logo && theme.logo !== '') {
      return '/static/img/logos-large/' + theme.logo;
    }
    return '/static/img/transparent-placeholder.png';
  }

  function InfoTip(text) {
    return html`<span class="info-tip" title="${text}"><i class="fal fa-info-circle"></i></span>`;
  }

  async function handleRegister() {
    setError('');
    setWarning('');
    setLoading(true);

    const email = document.querySelector('#reg-email').value.trim().toLowerCase();
    const password = document.querySelector('#reg-password').value;
    const confirmPassword = document.querySelector('#reg-confirm-password').value;

    if (!email || !email.includes('@')) {
      setError('Please enter a valid email address.');
      setLoading(false);
      return;
    }

    if (!passwordMeetsAllRules(password)) {
      setError('Password does not meet all the requirements shown below.');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      setLoading(false);
      return;
    }

    const userData = auth.userData;
    const users = userData.users || [];

    // Check if already registered (and active)
    const activeUser = users.find(u => u.email?.toLowerCase() === email && !u.removed);
    if (activeUser) {
      setError('This email is already registered with this school. Please log in instead.');
      setLoading(false);
      return;
    }

    // Check if removed
    const removedUser = users.find(u => u.email?.toLowerCase() === email && u.removed);
    if (removedUser) {
      const contactEmail = getContactEmail(userData);
      setError(`Your access to this school was revoked. Please contact ${contactEmail || 'your administrator'} to request re-access.`);
      setLoading(false);
      return;
    }

    // Check max users
    const maxUsers = userData.max_users_allowed || 0;
    const activeUsers = users.filter(u => !u.removed);
    if (maxUsers > 0 && activeUsers.length >= maxUsers) {
      const contactEmail = getContactEmail(userData);
      setError(`Maximum user limit (${maxUsers}) reached. Please contact ${contactEmail || 'your administrator'} to request access.`);
      setLoading(false);
      return;
    }

    // If no contact email is on file, the first registrant must self-identify
    // as the primary contact before proceeding.
    const contactEmail = getContactEmail(userData);
    if (!contactEmail && !isContactPerson) {
      setNoContactEmail(true);
      setWarning('No contact email is on file for this school. If you are the primary contact, tick the checkbox below to proceed. Otherwise, please ask your school\'s contact person to register first.');
      setLoading(false);
      return;
    }

    // Register in Cognito
    try {
      await signUp(email, password);
      setRegisteredEmail(email);
      setRegisteredPassword(password);
      setStep('verify');
    } catch (signUpErr) {
      if (signUpErr.code === 'UsernameExistsException') {
        setError('An account with this email already exists. Please go back and log in — you will be prompted to register with this school.');
      } else if (signUpErr.code === 'InvalidPasswordException') {
        setError('Password does not meet requirements. Use at least 8 characters with uppercase, lowercase, numbers, and a special character.');
      } else {
        setError(`Registration failed: ${signUpErr.message || 'Unknown error'}`);
      }
    }

    setLoading(false);
  }

  async function handleVerify() {
    setError('');
    setLoading(true);

    const code = document.querySelector('#verify-code').value.trim();

    if (!code) {
      setError('Please enter the verification code.');
      setLoading(false);
      return;
    }

    try {
      await confirmSignUp(registeredEmail, code);
    } catch (err) {
      if (err.code === 'CodeMismatchException') {
        setError('Invalid code. Please check and try again.');
      } else if (err.code === 'ExpiredCodeException') {
        setError('Code has expired. Please request a new one.');
      } else {
        setError(`Verification failed: ${err.message || 'Unknown error'}`);
      }
      setLoading(false);
      return;
    }

    // Add to ClientGroup.users (and set contact email if they are the contact person).
    // ST4S phase 3 (design §6.2): the registration split — the first ACTIVE
    // registrant is auto-approved; any subsequent registrant is written
    // status:'pending'. The entry shape + first/subsequent decision live in the
    // shared pure helper approval.js so BOTH registration paths stay in sync
    // (RegisterPage already lowercases via registeredEmail). The DB write MUST
    // still happen for pending users — that is what creates their pending entry.
    // The post-sign-in gate (loginStatusGate, applied in the MFA pages) then
    // routes a pending user to PendingApproval without a data session.
    const userData = auth.userData;
    const users = userData.users || [];
    const nowIso = new Date().toISOString();
    const { entry } = buildRegistrationEntry({ users, email: registeredEmail, nowIso });
    try {
      const updatedUsers = [...users, entry];
      const updateFields = { users: updatedUsers };
      if (isContactPerson && !getContactEmail(userData)) {
        const contacts = userData.contacts && Array.isArray(userData.contacts) ? [...userData.contacts] : [];
        contacts.push({ email: registeredEmail, position: 'Primary Contact' });
        updateFields.contacts = contacts;
      }
      await auth.db.collection('ClientGroups').updateOne(
        { _id: userData._id },
        { $set: updateFields }
      );
      userData.users = updatedUsers;
      if (updateFields.contacts) userData.contacts = updateFields.contacts;
    } catch (dbErr) {
      console.error('Failed to update ClientGroup users:', dbErr);
    }

    // ST4S reg-mfa-notify fix: ALL registrants (first AND pending) now sign in
    // and go through the SAME MFA flow — a pending user is no longer
    // short-circuited to PendingApproval before sign-in. That short-circuit
    // caused two bugs: (1) the pending account was created with NO MFA, and
    // (2) sendApprovalNotification ran with NO Cognito session, so database.js
    // could not attach the X-Id-Token header and hop 1 failed ("missing
    // token") — the contact person never got an email.
    //
    // The approval-notification send is therefore MOVED out of here to the
    // post-MFA completion sites (MFAVerifyPage / MFASetupPage), where a valid
    // ID token exists. We stash the entry + clientGroupId into auth so those
    // pages know what to send; the EXISTING login-completion gate there routes
    // a pending user to PendingApproval (no data session) and a first contact
    // into the portal.
    const clientGroupId = userData._id && userData._id['$oid'] ? userData._id['$oid'] : userData._id;

    // Sign in. Cognito issues the MFA challenge (MFA_SETUP on the first login,
    // MFA_REQUIRED afterwards). Both first and pending registrants take this
    // path now; the notification fires after MFA completes.
    try {
      await signIn(registeredEmail, registeredPassword);
      // Rare no-challenge path: a session already exists, so we are logged in.
      // Send the notification here (mirrors the MFA pages) and clear the stash.
      setAuth(prev => ({ ...prev, mfaPage: null, mfaVerified: true, userEmail: registeredEmail, userPassword: null, registrationEntry: null, registrationClientGroupId: null }));
      sendApprovalNotification(auth, entry, clientGroupId);
    } catch (signInErr) {
      if (signInErr.type === 'MFA_SETUP') {
        setAuth(prev => ({ ...prev, mfaPage: 'MFASetup', mfaChallenge: signInErr, userEmail: registeredEmail, userPassword: registeredPassword, registrationEntry: entry, registrationClientGroupId: clientGroupId }));
      } else if (signInErr.type === 'MFA_REQUIRED') {
        setAuth(prev => ({ ...prev, mfaPage: 'MFAVerify', mfaChallenge: signInErr, userEmail: registeredEmail, userPassword: registeredPassword, registrationEntry: entry, registrationClientGroupId: clientGroupId }));
      } else if (signInErr.type === 'NEW_PASSWORD_REQUIRED') {
        setAuth(prev => ({ ...prev, mfaPage: 'NewPassword', mfaChallenge: signInErr, userEmail: registeredEmail, userPassword: registeredPassword, registrationEntry: entry, registrationClientGroupId: clientGroupId }));
      } else {
        console.error('Unexpected sign-in error after verification:', signInErr);
        setError(`Email verified but sign-in failed: ${signInErr.message || signInErr.code || 'Unknown error'}. Please go back and log in.`);
      }
    }

    setLoading(false);
  }

  async function handleResend() {
    setError('');
    try {
      await resendConfirmationCode(registeredEmail);
      setWarning('A new code has been sent to your email.');
    } catch (err) {
      setError(`Failed to resend code: ${err.message || 'Unknown error'}`);
    }
  }

  function handleKeyPress(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      if (step === 'register') handleRegister();
      else handleVerify();
    }
  }

  function goBack() {
    setAuth(prev => ({ ...prev, mfaPage: null }));
  }

  return html`
    <div class="centre-box login">
      <img src="${getLogo()}" class="squiggle logo-large" />

      <div id="login">
        ${step === 'register' ? html`
          <h2 style="margin-bottom: 10px; font-size: 1.2em;">Register</h2>
          <p style="margin-bottom: 12px; font-size: 1em; opacity: 0.8;">
            Registering with <strong>${auth.userData?.name || 'your school'}</strong>
          </p>

          <span class="loginlabel">WORK EMAIL ${InfoTip('Use your school / organisation email address. e.g. \"j.smith@greenvalley.edu.au\"')}</span>
          <input type="email" id="reg-email" class="login-field" placeholder="you@yourschool.edu.au" /><br />

          <span class="loginlabel">PASSWORD</span>
          <input type="password" id="reg-password" class="login-field" placeholder="Choose a password" oninput=${(e) => setPw(e.target.value)} /><br />

          ${PasswordChecklist(pw, confirmPw)}

          <span class="loginlabel">CONFIRM PASSWORD</span>
          <input type="password" id="reg-confirm-password" class="login-field" placeholder="Confirm password" oninput=${(e) => setConfirmPw(e.target.value)} onkeyup=${handleKeyPress} /><br />

          <button id="login-submit" onclick=${handleRegister} disabled=${loading || !passwordMeetsAllRules(pw) || pw !== confirmPw}>
            ${loading ? 'Registering...' : html`<i class="fal fa-user-plus"></i>&ensp;REGISTER`}
          </button>

          ${noContactEmail ? html`
            <label class="contact-checkbox">
              <input type="checkbox" id="is-contact-person" onchange=${(e) => setIsContactPerson(e.target.checked)} />
              <span>I am the primary contact for this school</span>
            </label>
          ` : ''}
        ` : html`
          <h2 style="margin-bottom: 10px; font-size: 1.2em;">Verify Email</h2>
          <p style="margin-bottom: 12px; font-size: 1em; opacity: 0.8;">
            We've sent a verification code to <strong>${registeredEmail}</strong>
          </p>

          <span class="loginlabel">VERIFICATION CODE ${InfoTip('Check your email inbox (and spam folder) for a 6-digit code. e.g. \"482931\"')}</span>
          <input type="text" id="verify-code" class="login-field" placeholder="Enter 6-digit code" onkeyup=${handleKeyPress} /><br />

          <button id="login-submit" onclick=${handleVerify} disabled=${loading}>
            ${loading ? 'Verifying...' : html`<i class="fal fa-check"></i>&ensp;VERIFY`}
          </button>

          <button class="back-link" onclick=${handleResend}>
            Didn't receive a code? Resend
          </button>
        `}

        <button class="back-link" onclick=${goBack}>
          &larr; Back to Login
        </button>

        ${error ? html`<div class="error"><strong>ERROR</strong><p>${error}</p></div>` : ''}
        ${warning ? html`<div class="warning"><strong>WARNING</strong><p>${warning}</p></div>` : ''}
      </div>
    </div>
  `;
});

export default RegisterPage;
